import { useState, useCallback } from 'react';
import { exitIntentPopupApi } from '../api/exitIntentPopup';
import type { ExitIntentPopupSettings, PopupLead } from '../api/exitIntentPopup';

export function useExitIntentPopup() {
  const [settings, setSettings] = useState<ExitIntentPopupSettings | null>(null);
  const [leadsCount, setLeadsCount] = useState(0);
  const [leads, setLeads] = useState<PopupLead[]>([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsLastPage, setLeadsLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await exitIntentPopupApi.show();
      setSettings(res.data.data.settings);
      setLeadsCount(res.data.data.leads_count);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load exit-intent popup settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (next: ExitIntentPopupSettings) => {
    setSaving(true);
    setError(null);
    try {
      const res = await exitIntentPopupApi.update(next);
      setSettings(res.data.data);
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save exit-intent popup settings.');
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const fetchLeads = useCallback(async (page = 1) => {
    try {
      const res = await exitIntentPopupApi.leads(page);
      setLeads(res.data.data);
      setLeadsTotal(res.data.meta.total);
      setLeadsLastPage(res.data.meta.last_page);
      setLeadsPage(page);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load captured leads.');
    }
  }, []);

  return {
    settings, leadsCount, leads, leadsTotal, leadsPage, leadsLastPage,
    loading, saving, error, fetchSettings, save, fetchLeads, setError,
  };
}
